<script setup lang="ts">
  import { ref } from 'vue'
  import { IconDeactivate } from '@gat-ship/icons'
  import type { TenantUser } from '@gat-ship/types'
  import {
    Button,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderDescription,
    ModalHeaderIcon,
    ModalHeaderTitle,
    ModalSection
  } from '@gat-ship/vue-components'
  import { useTenantUserDetailContext } from '../../composables/useTenantUserDetail'

  const isOpen = ref(false)
  const tenantUserProvider = useTenantUserDetailContext()!
  const currentTenantUser = ref<TenantUser | undefined>(undefined)

  async function deactivateUser() {
    const tenantUser = currentTenantUser.value
    if (!tenantUser) return
    isOpen.value = false
    await tenantUserProvider.commands.deactivateUser({
      userId: tenantUser.userId
    })
  }

  export interface ShowDeactivateModalParams {
    tenantUser: TenantUser
  }

  function showModal({ tenantUser }: ShowDeactivateModalParams) {
    currentTenantUser.value = tenantUser
    isOpen.value = true
  }

  defineExpose({ showModal })
</script>

<template>
  <Modal v-model:open="isOpen">
    <ModalHeader>
      <ModalHeaderTitle>
        <ModalHeaderIcon>
          <IconDeactivate class="size-5" :stroke-width="1.5" />
        </ModalHeaderIcon>
        Deactivate User
      </ModalHeaderTitle>
      <ModalHeaderDescription v-if="currentTenantUser">
        {{ currentTenantUser.fullName }}
      </ModalHeaderDescription>
    </ModalHeader>
    <ModalContent>
      <ModalSection>
        <p class="whitespace-pre-line text-sm leading-tight">
          Deactivating this account will immediately block the user from signing
          in.
        </p>
        <p class="whitespace-pre-line text-sm leading-tight">
          The account and its data remain intact and can be reactivated later if
          needed.
        </p>
        <p class="whitespace-pre-line text-sm leading-tight">
          Are you sure you want to deactivate this user?
        </p>
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <Button variant="outline" @click="deactivateUser">
        Deactivate User
      </Button>
      <Button variant="ghost" @click="isOpen = false"> Cancel </Button>
    </ModalFooter>
  </Modal>
</template>
